import styled from '@emotion/styled';
import { ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import Alert from '../_sonic/_molecules/Feedback/Alert/Alert';
import LoadingScreen from '../LoadingScreen';
import { translateError, useXsrf } from './authenticationUtils';
import { Container, Subtitle, Title, TitleLarge } from './styledComponents';

const Switch = styled.nav`
  display: flex;
  margin-bottom: 40px;
`;

const SwitchAnchor = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>`
  ${({ theme }) => theme.textStyles.labelM}
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  font-weight: bold;
  height: 34px;
  width: 200px;
  background: ${({ selected, theme }) =>
    selected ? theme.colors.blueDark100 : 'transparent'};
  color: ${({ selected, theme }) =>
    selected ? '#FCFCFC' : theme.colors.blueDark100};
  border: 2px solid
    ${({ selected, theme }) =>
      selected ? theme.colors.blueDark100 : theme.colors.monochrome30};
  border-radius: 0 8px 8px 0;
  &:first-of-type {
    border-radius: 8px 0 0 8px;
  }
`;

const BoldLink = styled(Link)`
  ${({ theme }) => theme.textStyles.labelLEmphasized}
  color: ${({ theme }) => theme.colors.blueDark100};
`;

const SwitchText = styled.div`
  margin-top: 40px;
  ${({ theme }) => theme.textStyles.labelL};
  color: ${({ theme }) => theme.colors.blueDark70};
`;

function SignInInstead() {
  const { search } = useLocation();
  return (
    <SwitchText>
      Already have an Oura account?{' '}
      <BoldLink to={`/user/invitation/sign-in${search}`}>Sign in</BoldLink>
    </SwitchText>
  );
}

function SignUpInstead() {
  const { search } = useLocation();
  return (
    <SwitchText>
      Don’t have an Oura account?{' '}
      <BoldLink to={`/user/invitation/sign-up${search}`}>
        Create account
      </BoldLink>
    </SwitchText>
  );
}

interface ErrorProps {
  error: string;
}

function ErrorSection({ error }: ErrorProps) {
  return (
    <Alert status="error" hideClose mb={4}>
      {translateError(error)}
    </Alert>
  );
}

type AuthenticationType = 'signin' | 'signup';

function AuthenticationSwitch({ type }: { type: AuthenticationType }) {
  const { search } = useLocation();
  return (
    <Switch>
      <SwitchAnchor
        to={`/user/invitation/sign-in${search}`}
        selected={type === 'signin'}
        aria-current={type === 'signin' ? 'page' : false}
      >
        Sign in
      </SwitchAnchor>
      <SwitchAnchor
        to={`/user/invitation/sign-up${search}`}
        selected={type === 'signup'}
        aria-current={type === 'signup' ? 'page' : false}
      >
        Create account
      </SwitchAnchor>
    </Switch>
  );
}

function ChildrenWrapper({
  render,
  error,
  token,
  xsrf,
}: {
  error: string | null;
  token: string;
  xsrf: string;
  render: (props: {
    error: string | null;
    token: string;
    xsrf: string;
  }) => ReactNode;
}) {
  return <>{render({ error, token, xsrf })}</>;
}

export default function InvitationAuthenticationPageBase({
  children,
  type,
}: {
  type: AuthenticationType;
  children: (props: {
    error: string | null;
    token: string;
    xsrf: string;
  }) => ReactNode;
}) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const error = searchParams.get('error');
  const { loading: loadingXsrf, error: xsrfError, xsrfToken } = useXsrf();

  if (loadingXsrf) {
    return <LoadingScreen />;
  }

  if (xsrfError || !xsrfToken) {
    throw new Error('There was an issue with the XSRF token');
  }

  if (!token) {
    return (
      <Container narrow>
        <Title>The link you followed may be broken.</Title>
        <Subtitle>
          This may be due to the link being incomplete or already used. Check
          that you copied the link correctly. If the problem persists, contact
          us at support@ouraring.com.
        </Subtitle>
      </Container>
    );
  }

  return (
    <Container narrow>
      <TitleLarge>Welcome!</TitleLarge>
      <AuthenticationSwitch type={type} />
      {error != null && <ErrorSection error={error} />}
      <ChildrenWrapper
        render={children}
        error={error}
        token={token}
        xsrf={xsrfToken}
      />
      {type === 'signin' ? <SignUpInstead /> : <SignInInstead />}
    </Container>
  );
}
